import React from 'react';

import { isEmpty } from 'lodash';
import clsx from 'clsx';
import { Link, useLocation, matchPath } from 'react-router-dom';
import { compose } from 'recompose';
import { inject, observer } from 'mobx-react';
import OpenInNewIcon from '@material-ui/icons/OpenInNew';

import { useStyles } from './styles';
import { hasScopeAndActionPermission } from 'Utils/scopeAndPermission';

function TopNavMenu(props) {
  const { menus, userStore, permissionStore, isOpacity, borderBottom, isParentMenu = false, handleMenu, ...otherProps } = props;
  const { isLoading, user } = userStore;

  if (isLoading) return null;

  const menuToRender = filterByPermissions(menus, user?.scopes || [], permissionStore);

  // Collect all leaf links so MenuItem can detect sibling prefix conflicts
  const siblingLinks = menuToRender.filter((m) => !m.subMenus).map((m) => m.link);

  return (
    <div>
      {menuToRender.map((config) => {
        return (
          <MenuItem
            key={config.label}
            config={config}
            isOpacity={isOpacity}
            borderBottom={borderBottom}
            isParentMenu={isParentMenu}
            handleMenu={handleMenu}
            siblingLinks={siblingLinks}
            {...otherProps}
          />
        );
      })}
    </div>
  );
}

export default compose(inject('userStore', 'permissionStore'), observer)(TopNavMenu);

const MenuItem = compose(
  inject('userStore', 'permissionStore'),
  observer,
)((props) => {
  const { config, isOpacity, borderBottom, isParentMenu = false, handleMenu, permissionStore, userStore, siblingLinks = [], ...otherProps } = props;
  const { user } = userStore;
  const userScopes = user?.scopes || [];

  const location = useLocation();

  const classes = useStyles({ ...props, isOpacity, borderBottom });

  const filterSubMenus = isParentMenu ? filterByPermissions(config?.subMenus, userScopes || [], permissionStore, false) : [];
  const toLink = isParentMenu && !isEmpty(filterSubMenus) ? filterSubMenus[0]?.link : config.link;

  // Hide parent menu item if it has sub menus but no sub menus are visible
  if (!isEmpty(config?.subMenus) && isEmpty(filterSubMenus)) return null;

  // Handle external links
  const isExternalLink = config.link?.startsWith('http');
  const linkProps = isExternalLink ? { as: 'a', href: config.link, target: '_blank', rel: 'noopener noreferrer' } : { to: toLink };

  const LinkComponent = isExternalLink ? 'a' : Link;
  // Use exact matching only when a sibling link starts with this link + '/'
  // (e.g. /linehauls has siblings /linehauls/summary, so it must be exact-matched)
  // All other items use non-exact so child paths like /linehauls/summary/:id stay active
  const useExactMatch = !config.subMenus && siblingLinks.some(
    (link) => link !== config.link && link.startsWith(config.link + '/'),
  );

  const _isActive = !isExternalLink && isActive(
    config.subMenus ? config.subMenus?.map((subMenu) => subMenu.link) : config.link,
    location.pathname,
    useExactMatch,
  );

  return (
    <LinkComponent
      key={config.link}
      className={clsx({
        [classes.menu]: true,
        [classes.active]: _isActive,
      })}
      {...linkProps}
      onClick={() => (typeof handleMenu === 'function' ? handleMenu(isParentMenu) : null)}
      {...otherProps}
    >
      {config.label}
      {isExternalLink && (
        <OpenInNewIcon style={{ fontSize: 13, marginLeft: 4, verticalAlign: 'middle', color: 'inherit' }} />
      )}
    </LinkComponent>
  );
});

const filterByPermissions = (menus, userScopes, permissionStore, isParentLevel = true) => {
  return (
    menus?.filter((item) => {
      // Filter out hidden items from menu display
      if (item?.hidden) return false;

      // For parent menus with submenus, only check if submenus are visible
      if (isParentLevel && !isEmpty(item?.subMenus)) {
        const visibleSubmenus = filterByPermissions(item.subMenus, userScopes, permissionStore, false);
        return !isEmpty(visibleSubmenus);
      }

      // For submenus or items without submenus, check their permissions
      return hasScopeAndActionPermission({
        scopes: item?.scopes,
        actionPermission: item?.actionPermission,
        userScopes,
        permissionStore,
      });
    }) || []
  );
};

const isValid = (_link, _pathname, exact = false) => {
  if (!_pathname || !_link) return false;
  if (_link.startsWith('http')) return false;

  return Boolean(matchPath(_pathname, { path: _link, exact, strict: false }));
};

export const isActive = (links, pathname, exact = false) => {
  return Array.isArray(links) ? links?.some((link) => isValid(link, pathname, exact) && !link?.startsWith('http')) : isValid(links, pathname, exact);
};
